import { useState, useEffect } from 'react'

/** Format milliseconds as MM:SS for countdown display. */
export function formatBlindTimerMs(remainingMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, remainingMs) / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Resolve the level's start time on *this device's* clock.
 *
 * `lastIncreaseTime` and `offset` are server timestamps, so on a device whose
 * clock runs behind the server the start lands slightly in the future and the
 * raw elapsed time goes negative. Capping the anchor to the client's "now" at
 * the moment the level is first observed keeps elapsed >= 0, so the countdown
 * begins at the full interval and ticks down immediately instead of overshooting
 * and then stalling at the top of the level.
 *
 * Capture this once per level (when the timestamp/offset changes), not on every
 * tick — recomputing it each second would re-pin the anchor to "now" and freeze
 * the display.
 */
export function resolveBlindStartAnchor(lastIncreaseTime: number, offset: number, clientNow: number): number {
  return Math.min(lastIncreaseTime + offset, clientNow)
}

/**
 * Remaining ms until the next blind increase, measured from a fixed start anchor
 * and capped to the configured interval so it never displays above the duration.
 */
export function calculateBlindRemainingMs(timeBetween: number, startAnchor: number, now: number): number {
  return Math.max(0, Math.min(timeBetween, timeBetween - (now - startAnchor)))
}

/**
 * Hook to calculate remaining time until blind increase
 * Returns formatted time string and updates every second
 */
export function useBlindTimer(
  game: {
    timeBetweenBlindsIncrease?: number
    previousBlindIncreaseTimestamp?: number
    blindIncreaseOffset?: number
    pausedAt?: number
  } | null
) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  // Extract values to use in dependency array
  const timeBetween = game?.timeBetweenBlindsIncrease ?? 0
  const lastIncreaseTime = game?.previousBlindIncreaseTimestamp ?? 0
  const offset = game?.blindIncreaseOffset ?? 0
  const pausedAt = game?.pausedAt

  useEffect(() => {
    // If blind increase is disabled, no timer needed
    if (timeBetween === 0) {
      setRemainingMs(null)
      return
    }

    // Anchor the level start to this device's clock once per level, so server/
    // client clock skew can't inflate the countdown or stall it at the top.
    const startAnchor = resolveBlindStartAnchor(lastIncreaseTime, offset, Date.now())

    const calculateRemaining = () => {
      // If game is paused, freeze the timer at the paused moment
      const now = pausedAt || Date.now()

      setRemainingMs(calculateBlindRemainingMs(timeBetween, startAnchor, now))
    }

    // Calculate immediately
    calculateRemaining()

    // Update every second (only if not paused)
    if (!pausedAt) {
      const interval = setInterval(calculateRemaining, 1000)
      return () => clearInterval(interval)
    }
  }, [timeBetween, lastIncreaseTime, offset, pausedAt])

  // Format remaining time as MM:SS
  if (remainingMs === null) {
    return 'Off'
  }

  return formatBlindTimerMs(remainingMs)
}
